import { Fragment, useState } from "react";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild,
} from "@headlessui/react";
import {
    XMarkIcon,
    Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import {
    Button,
    Input,
    Checkbox,
    ScrollShadow,
    Select,
} from "components/ui";
import clsx from "clsx";
import { TaxesModal } from "./TaxesModal";

export function AdvancedSettingsDrawer({
    show,
    onClose,
}) {
    const [showTaxes, setShowTaxes] = useState(false);

    return (
        <>
            <Transition show={show} as={Fragment}>
                <Dialog as="div" className="relative z-[400]" onClose={onClose}>
                    <TransitionChild
                        as={Fragment}
                        enter="ease-in-out duration-500"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/25 backdrop-blur-sm dark:bg-black/40" />
                    </TransitionChild>

                    <div className="fixed inset-0 overflow-hidden text-left">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                                <TransitionChild
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <DialogPanel className="pointer-events-auto w-screen max-w-md isolate">
                                        <div className="flex h-full flex-col bg-white shadow-2xl dark:bg-dark-800 overflow-hidden border-l border-slate-100 dark:border-dark-600">
                                            {/* Header */}
                                            <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-5 dark:border-dark-700 dark:bg-dark-900/50">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2.5 rounded-xl bg-primary-50 dark:bg-primary-500/10 text-primary">
                                                            <Cog6ToothIcon className="h-5 w-5" />
                                                        </div>
                                                        <div>
                                                            <DialogTitle className="text-lg font-bold text-gray-900 dark:text-dark-100 leading-tight">
                                                                Advanced Settings
                                                            </DialogTitle>
                                                            <p className="text-[11px] font-medium text-gray-500 dark:text-dark-400 uppercase tracking-widest mt-0.5">
                                                                Configure job preferences
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={onClose}
                                                        className="group relative rounded-full p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all active:scale-95"
                                                    >
                                                        <XMarkIcon className="h-5 w-5 transition-transform group-hover:rotate-90" />
                                                    </button>
                                                </div>
                                            </div>

                                            <ScrollShadow className="flex-1 overflow-y-auto px-6 py-8 no-scrollbar space-y-10 scrollbar-sm">
                                                {/* Project Management Options */}
                                                <div className="space-y-6">
                                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 pl-1">Project management options</h3>
                                                    <div className="space-y-4">
                                                        <div className="space-y-1.5 flex flex-col pl-1">
                                                            <span className="text-[10px] font-bold text-slate-500 dark:text-dark-400 uppercase tracking-widest mb-2">Time clock</span>
                                                            <Checkbox label="Enable geofencing on Time Clock shifts" color="primary" classNames={{ labelText: "text-[13px] font-semibold text-slate-700 dark:text-dark-200" }} />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Template Options */}
                                                <div className="space-y-6">
                                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 pl-1">Template options</h3>
                                                    <div className="pl-1">
                                                        <Checkbox label="Make this job a working template" color="primary" classNames={{ labelText: "text-[13px] font-semibold text-slate-700 dark:text-dark-200" }} />
                                                    </div>
                                                </div>

                                                {/* Margin and Markup */}
                                                <div className="space-y-6">
                                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 pl-1">Margin and markup</h3>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <Select label="Percentage type" data={["Markup", "Margin"]} defaultValue="Markup" />
                                                        <Input label="Percentage" type="number" suffix="%" defaultValue="0.00" className="h-11" />
                                                    </div>
                                                </div>

                                                {/* Taxes */}
                                                <div className="space-y-6">
                                                    <div className="flex items-center justify-between px-1">
                                                        <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Taxes</h3>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); setShowTaxes(true); }}
                                                            className="text-[10px] font-bold text-primary hover:underline transition-all"
                                                        >
                                                            Manage
                                                        </button>
                                                    </div>
                                                    <Select label="Default tax rate" data={["No Tax", "Standard Tax"]} defaultValue="No Tax" />
                                                </div>

                                                {/* Budget */}
                                                <div className="space-y-6">
                                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 pl-1">Budget</h3>
                                                    <div className="space-y-4">
                                                        <Select label="Projection reference default" data={["System projection", "Custom projection"]} defaultValue="System projection" />
                                                        <div className="pl-1">
                                                            <Checkbox label="Include Time Clock labor in the Job Costing Budget" color="primary" classNames={{ labelText: "text-[13px] font-semibold text-slate-700 dark:text-dark-200" }} />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Purchase Orders */}
                                                <div className="space-y-6">
                                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 pl-1">Purchase orders</h3>
                                                    <div className="space-y-4">
                                                        <Input label="Individual Purchase Order limit" type="number" prefix="$" placeholder="Unlimited" className="h-11" />
                                                        <Input label="Total Job Purchase Order limit" type="number" prefix="$" placeholder="Unlimited" className="h-11" />
                                                    </div>
                                                </div>
                                            </ScrollShadow>

                                            {/* Footer */}
                                            <div className="border-t border-gray-100 bg-gray-50 px-6 py-5 dark:border-dark-700 dark:bg-dark-900/50 shrink-0">
                                                <div className="flex items-center gap-3">
                                                    <Button
                                                        variant="flat"
                                                        type="button"
                                                        onClick={onClose}
                                                        className="flex-1 h-11 rounded-xl text-gray-500 font-bold tracking-tight hover:bg-white dark:hover:bg-dark-700 transition-all active:scale-95 border border-gray-200 dark:border-dark-600 px-0 shadow-sm"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        onClick={onClose}
                                                        variant="filled"
                                                        className={clsx(
                                                            "flex-[1.5] h-11 rounded-xl font-bold tracking-tight transition-all active:scale-95",
                                                            "bg-primary hover:bg-primary-600 shadow-xl shadow-primary/20"
                                                        )}
                                                    >
                                                        Apply
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </DialogPanel>
                                </TransitionChild>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <TaxesModal show={showTaxes} onClose={() => setShowTaxes(false)} />
        </>
    );
}
